import log from "loglevel";

export interface CacheItem<T> {
    data: T;
    timestamp: number;
}

export const STORAGE_KEYS = {
    USER: 'user',
    COURSES_CACHE: 'courses_cache',
    LESSONS_CACHE_PREFIX: 'lessons_cache_',
    AI_ASSISTANT_USAGE: 'ai_assistant_usage',
} as const;

// Get encryption key from environment variables with fallback for development
const getEncryptionKey = (): string => {
    return process.env.REACT_APP_STORAGE_ENCRYPTION_KEY || '';
};

// Generate a unique salt for each session
const SESSION_SALT = Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');

const encrypt = (text: string): string => {
    const key = getEncryptionKey();
    log.info("encrypt ", key);
    const textToChars = (text: string) => text.split('').map(c => c.charCodeAt(0));
    const byteHex = (n: number) => ("0" + Number(n).toString(16)).substr(-2);
    const applySaltToChar = (code: number) => {
        return textToChars(key + SESSION_SALT).reduce((a, b) => a ^ b, code);
    };

    return SESSION_SALT + text
        .split('')
        .map(c => textToChars(c)[0])
        .map(applySaltToChar)
        .map(byteHex)
        .join('');
};

const decrypt = (encoded: string): string => {
    const key = getEncryptionKey();
    // Extract the session salt (first 32 characters, 16 bytes in hex)
    const salt = encoded.slice(0, 32);
    const encryptedData = encoded.slice(32);
    
    const textToChars = (text: string) => text.split('').map(c => c.charCodeAt(0));
    const applySaltToChar = (code: number) => {
        return textToChars(key + salt).reduce((a, b) => a ^ b, code);
    };
    
    return encryptedData
        .match(/.{1,2}/g)!
        .map(hex => parseInt(hex, 16))
        .map(applySaltToChar)
        .map(charCode => String.fromCharCode(charCode))
        .join('');
};

export const saveByEncrypt = (key: string, data: any) => {
    try {
        const encryptedData = encrypt(JSON.stringify(data));
        localStorage.setItem(key, encryptedData);
    } catch (error) {
        log.error('Error saving to storage:', error);
    }
};

export const saveToStorage = (key: string, data: any) => {
    try {
        const jsonData = JSON.stringify(data);
        localStorage.setItem(key, jsonData);
    } catch (error) {
        log.error('Error saving to storage:', error);
    }
};

export const loadByDecrypt = (key: string) => {
    try {
        const encryptedData = localStorage.getItem(key);
        if (!encryptedData) return null;
        const decryptedData = decrypt(encryptedData);
        return JSON.parse(decryptedData);
    } catch (error) {
        log.error('Error loading from storage:', error);
        return null;
    }
};

export const loadFromStorage = (key: string) => {
    try {
        const jsonData = localStorage.getItem(key);
        if (!jsonData) return null;
        return JSON.parse(jsonData);
    } catch (error) {
        log.error('Error loading from storage:', error);
        return null;
    }
};

export const removeFromStorage = (key: string) => {
    try {
        localStorage.removeItem(key);
    } catch (error) {
        log.error('Error removing from storage:', error);
    }
};

export const cacheStorage = {
    setCache<T>(key: string, data: CacheItem<T>): void {
        try {
            localStorage.setItem(key, JSON.stringify(data));
        } catch (error) {
            log.warn('Failed to save to localStorage:', error);
        }
    },

    getCache<T>(key: string): CacheItem<T> | null {
        try {
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : null;
        } catch (error) {
            log.warn('Failed to get from localStorage:', error);
            return null;
        }
    }
}; 